import { createDataProvider } from '@modules/api-core'
import type { TApiErrorResponse, TCustomError } from '@modules/types'
import { API_BASE_URL, SHARE_API_BASE_URL } from './api'

const publisherErrorHandler = async (response: Response): Promise<never> => {
  let errorData: TApiErrorResponse

  try {
    errorData = await response.json()
  } catch {
    const error = new Error(`HTTP error! status: ${response.status}`) as TCustomError
    error.status = response.status
    error.statusCode = response.status
    throw error
  }

  if (errorData.error && typeof errorData.error === 'object' && errorData.error !== null) {
    const nestedError = errorData.error as Record<string, unknown>
    let errorMessage =
      (typeof nestedError.message === 'string' ? nestedError.message : '') ||
      (typeof nestedError.name === 'string' ? nestedError.name : '') ||
      `HTTP error! status: ${response.status}`

    if (nestedError.errors && typeof nestedError.errors === 'object') {
      const validationErrors = Object.entries(nestedError.errors)
        .map(([field, messages]) => {
          if (Array.isArray(messages)) {
            return `${field}: ${messages.join(', ')}`
          }
          return `${field}: ${messages}`
        })
        .join('; ')

      if (validationErrors) {
        errorMessage += `\nValidation errors: ${validationErrors}`
      }
    }

    const error = new Error(errorMessage) as TCustomError
    error.status = typeof nestedError.status === 'number' ? nestedError.status : response.status
    error.statusCode = response.status
    error.data = nestedError
    throw error
  }

  const errorMessage =
    errorData.message || errorData.error || `HTTP error! status: ${response.status}`
  const error = new Error(errorMessage) as TCustomError
  error.status = response.status
  error.statusCode = response.status
  error.data = errorData
  throw error
}

const publisherFilterHandler = (
  filters: Array<{ field?: string; operator?: string; value?: unknown }>,
  url: URL,
) => {
  filters.forEach(filter => {
    if ('field' in filter && 'operator' in filter && 'value' in filter) {
      if (filter.field === 'hide_closed') {
        const hideValue = filter.value === true || filter.value === 'true' ? '1' : '0'
        url.searchParams.append('filter[hide_closed]', hideValue)
      } else if (filter.operator === 'contains' && filter.field === 'q') {
        url.searchParams.append('filter[q]', String(filter.value))
      } else if (filter.operator === 'eq') {
        url.searchParams.append(`filter[${filter.field}]`, String(filter.value))
      }
    }
  })
}

export const customDataProvider = createDataProvider({
  baseUrl: API_BASE_URL,
  customErrorHandler: publisherErrorHandler,
  customFilterHandler: publisherFilterHandler,
})

export const shareDataProvider = createDataProvider({
  baseUrl: SHARE_API_BASE_URL,
  customErrorHandler: publisherErrorHandler,
  customFilterHandler: publisherFilterHandler,
})
